import { Component, inject, Input, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

// FontAwesome
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowRight, faSearch } from '@fortawesome/free-solid-svg-icons';

// Components
import { NavbarComponent } from "../../../template/components/navbar/navbar.component";
import { ConfirmModal } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { ListComponent } from '../../../shared/components/list/list.component';

// Services
import { GroupService } from '../../services/group.service';
import { UserService } from '../../../users/services/user.service';
import { ToastService } from '../../../shared/services/toast.service';

// Models
import { CreateGroup, GroupDetail } from '../../models/group.model';
import { User } from '../../../users/models/user.model';

type EditGroupForm = {
  [K in keyof CreateGroup]: FormControl<CreateGroup[K]>;
};


@Component({
    selector: 'app-group-detail',
    imports: [
        NavbarComponent,
        AsyncPipe,
        FormsModule,
        ReactiveFormsModule,
        FontAwesomeModule,
        ListComponent
    ],
    templateUrl: './group-detail.component.html',
    styleUrl: './group-detail.component.scss'
})
export class GroupDetailComponent implements OnInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly groupSrv = inject(GroupService);
  private readonly userSrv = inject(UserService);
  private readonly toastSrv = inject(ToastService);
  private readonly modalSrv = inject(NgbModal);
  private readonly router = inject(Router);

  faArrowRight = faArrowRight;
  faSearch = faSearch;

  @Input() id!: string;

  groupDetail$!: Observable<GroupDetail>;
  users$!: Observable<User[]>;

  searchValue: string = '';

  editMode = false;
  loading = false;

  navList = [{ label: 'Lista Gruppi', routePath: '/groups' }];

  groupForm: FormGroup<EditGroupForm> = this.fb.group({
    name: [{ value: '', disabled: true }, Validators.required],
    description: [{ value: '', disabled: true }]
  });

  ngOnInit(): void {
    // Call the getGroupDetail method from groupSrv service, passing the current group id (this.id)
    this.groupDetail$ = this.groupSrv.getGroupDetail(this.id)
      .pipe(
        tap(groupDetail => {
          this.navList = [{ label: 'Lista Gruppi', routePath: '/groups' }, { label: `${groupDetail.name}`, routePath: `/group/${this.id}` }];
          this.groupForm.patchValue(groupDetail);
        }));

    this.users$ = this.userSrv.getGroupAssociableUsers(this.id);;
  }

  getFilteredUsers(users: User[]) {
    if (!this.searchValue) return users;
    const lowerCaseSearchValue = this.searchValue.toLowerCase();
    return users.filter(user =>
      user.name.toLowerCase().includes(lowerCaseSearchValue) ||
      user.surname.toLowerCase().includes(lowerCaseSearchValue)
    );
  }

  updateGroup() {
    if (this.groupForm.valid) {
      this.groupSrv.updateGroup(this.groupForm.getRawValue(), this.id)
        .subscribe(
          {
            next: (res) => {
              this.toastSrv.show({ type: 'success', message: 'Gruppo aggiornato con successo' });
              this.groupForm.patchValue(res);
              this.toggleEditMode(false);
            },
            error: (error) => console.log(error)
          });
    }
  }

  addUserToGroup(userId: string) {
    this.apiToggleUserToGroup(userId, 'add');
  }

  removeUserFromGroup(user: User, action: 'add' | 'remove') {
    const modalRef = this.modalSrv.open(ConfirmModal, { centered: true, windowClass: 'qai-modal', size: 'lg' });
    modalRef.componentInstance.title = 'Dissocia Utente';
    modalRef.componentInstance.body = `Stai disassociando l'utente ${user.email} dal gruppo ${this.groupForm.controls['name'].getRawValue()}.`; // TODO: trovare modo alternativo per recuperare il nome gruppo
    modalRef.result.then(
      (result) => {
        this.apiToggleUserToGroup(user.uid, action);
        console.log(`Closed with: ${result}`);
      },
      (reason) => {
        console.log(`Dismissed`);
      },
    );
  }

  toggleEditMode(value: boolean) {
    this.editMode = value;
    if (value) {
      this.groupForm.enable();
    } else {
      this.groupForm.disable();
    }
  }

  goToUserDetail(user: User) {
    this.router.navigate(['/users', user.uid]);
  }

  private apiToggleUserToGroup(userId: string, action: 'add' | 'remove') {
    this.loading = true;
    this.groupSrv.toggleUserToGroup(this.id, { userId, action })
      .pipe(finalize(() => this.loading = false))
      .subscribe(res => {
        this.toastSrv.show({ type: 'success', message: 'Associazione utente / gruppo aggiornata' });
        this.groupDetail$ = this.groupSrv.getGroupDetail(this.id);
        this.users$ = this.userSrv.getGroupAssociableUsers(this.id);;
      });
  }
}
